import { Logger } from '@suntex.core/logging';
import { DataRecord } from '@suntex.core/types';
import {
  Entities_Login_ByToken_Response,
  PlayerId,
  UserId,
} from '@suntex.fim/api-client';

import { LocalStorageAdapter } from './adapters/local-storage-adapter';
import { extractLoginInfo, getTokenExpiryMs } from './jwt-utils';
import {
  AuthConfig,
  AuthError,
  AuthState,
  BasicLoginInfo,
  IAuthApiClient,
  IAuthService,
  IAuthStorage,
  SignInOptions,
  SignOutOptions,
  isPlayerLoginType,
} from './types';

const logger = Logger.for(() => AuthService);

/**
 * Core authentication service implementation
 */
export class AuthService implements IAuthService {
  private state: AuthState & DataRecord;
  private subscribers: Set<(state: AuthState) => void> = new Set();
  private apiClient: IAuthApiClient;
  private storage: IAuthStorage;
  private isRefreshing = false;
  private isDisposed = false;
  private lastRefreshAttemptMs = 0;
  private unsubscribeStorage: (() => void) | null = null;

  // eslint-disable-next-line no-magic-numbers
  private static readonly REFRESH_THRESHOLD_MS = 15 * 60 * 1000; // 15 minutes
  // eslint-disable-next-line no-magic-numbers
  private static readonly REFRESH_COOLDOWN_MS = 30 * 1000; // 30 seconds

  constructor(config: AuthConfig) {
    this.apiClient = config.apiClient;
    this.storage = config.storage ?? new LocalStorageAdapter();

    try {
      // Initialize state from storage
      const jwt = this.storage.jwt;
      const login = this.storage.login;

      this.state = {
        isAuthenticated: !!jwt && !!login,
        isLoading: false,
        signingOut: false,
        jwt: jwt || null,
        login: login ? { ...login } : null,
      };
    } catch (e) {
      logger.warn('Error initializing auth state.', {}, e);
      this.state = {
        isAuthenticated: false,
        isLoading: false,
        signingOut: false,
        jwt: null,
        login: null,
      };
    }

    this.unsubscribeStorage =
      this.storage.onChange?.(() => this.syncFromStorage()) ?? null;
  }

  getState(): AuthState & DataRecord {
    this.maybeRefreshToken();
    return { ...this.state };
  }

  setPlayerId(playerId: PlayerId): void {
    const { login } = this.state;
    if (!login) {
      this.throwAuthError('Not authenticated');
    }
    this.setState({ login: { ...login, playerId } });
  }

  subscribe(callback: (state: AuthState) => void): () => void {
    this.subscribers.add(callback);
    return () => {
      this.subscribers.delete(callback);
    };
  }

  private setState(newState: Partial<AuthState & DataRecord>): void {
    this.state = {
      ...this.state,
      ...newState,
    };
    this.notifySubscribers();
  }

  private notifySubscribers(): void {
    const currentState = { ...this.state };
    this.subscribers.forEach((callback) => {
      try {
        callback(currentState);
      } catch (e) {
        logger.warn(
          'Subscriber callback error',
          { callback: callback.name || '<anonymous>', currentState },
          e,
        );
        // Subscriber error - continue with other subscribers
        // Individual subscribers should handle their own errors
      }
    });
  }

  async initialize(): Promise<AuthState> {
    this.setLoading(true);

    try {
      const loginToken = this.storage.loginToken;
      const jwt = this.storage.jwt;

      if (loginToken) {
        try {
          const response = await this.signInWithToken(loginToken);
          if (!response.status) {
            this.clearAuthState();
          }
        } catch (e) {
          this.clearAuthState();
          throw e;
        }
      } else if (jwt) {
        this.setState({
          jwt: jwt,
          isAuthenticated: true,
        });
      } else {
        this.clearAuthState();
      }
    } finally {
      this.setLoading(false);
    }

    return this.getState();
  }

  async refresh(): Promise<void> {
    const { jwt, login } = this.getState();
    if (!jwt || !login) {
      this.throwAuthError('Not authenticated');
    }
    this.setLoading(true);
    try {
      await this.handleSuccessfulAuth(jwt, {
        loginId: login.id,
        loginType: login.loginType,
      });
    } catch (e) {
      this.clearAuthState();
      throw e;
    } finally {
      this.setLoading(false);
    }
  }

  async signIn(options: SignInOptions): Promise<AuthState> {
    if ('jwt' in options) {
      await this.signInWithJwt(options.jwt);
    } else {
      await this.signInWithCredentials(options.userId, options.password);
    }

    return this.getState();
  }

  async signOut(options?: SignOutOptions): Promise<AuthState> {
    const reason = options?.reason;
    this.setState({ signingOut: true, signOutReason: reason });
    // Yield before clearing state so subscribers see signingOut:true while isAuthenticated is
    // still true — components can safely gate on signingOut to avoid spurious auth errors.
    await Promise.resolve();
    this.clearAuthState(reason ? { signOutReason: reason } : undefined);
    return this.getState();
  }

  dispose(): void {
    this.isDisposed = true;
    this.subscribers.clear();
    this.unsubscribeStorage?.();
    this.unsubscribeStorage = null;
  }

  private async signInWithJwt(jwt: string): Promise<void> {
    this.setLoading(true);

    try {
      const loginInfo = extractLoginInfo(jwt);
      await this.handleSuccessfulAuth(jwt, loginInfo);
    } catch (e) {
      this.clearAuthState();
      this.throwAuthError(
        'Failed to authenticate with JWT',
        'JWT_AUTH_FAILED',
        e,
      );
    } finally {
      this.setLoading(false);
    }
  }

  private async signInWithCredentials(
    userId: UserId,
    password: string,
  ): Promise<void> {
    this.setLoading(true);

    try {
      // Trim outer whitespace from credentials to avoid accidental spaces
      const trimmedUserId = userId.trim() as UserId;
      const trimmedPassword = password.trim();
      const response = await this.apiClient.post_Login_ByUserIdAndPassword({
        userId: trimmedUserId,
        password: trimmedPassword,
      });

      if (!response.status) {
        this.throwAuthError(response.message);
      }

      if (!response.token) {
        this.throwAuthError('The login token could not be created');
      }
      await this.handleSuccessfulAuth(response.token, response.userInfo);
    } catch (e) {
      this.clearAuthState();
      throw e;
    } finally {
      this.setLoading(false);
    }
  }

  private async signInWithToken(
    token: string,
  ): Promise<Entities_Login_ByToken_Response> {
    try {
      const response = await this.apiClient.post_Login_ByToken({
        loginToken: token,
      });

      if (!response.status || !response.token) {
        response.status = false;
        response.message =
          response.message || 'The login token could not be created';
        return response;
      }

      await this.handleSuccessfulAuth(response.token, response.userInfo);
      return response;
    } catch (error) {
      this.throwAuthError(
        'Failed to authenticate with token',
        'TOKEN_AUTH_FAILED',
        error,
      );
    }
  }

  private async handleSuccessfulAuth(
    jwt: string,
    { loginType, loginId }: BasicLoginInfo,
  ): Promise<void> {
    this.storage.jwt = jwt;
    this.setState({
      jwt,
      isAuthenticated: false,
      login: null,
    });
    // Fetch and store login information
    const login = await this.apiClient.get_Login_StateInfo({
      loginType,
      loginId,
    });

    // Handle player-specific logic
    if (isPlayerLoginType(loginType)) {
      login.playerId = login.entityId as PlayerId;
    }

    this.storage.login = login;
    this.setState({
      isAuthenticated: true,
      login: { ...login },
      signOutReason: undefined,
    });
  }

  private clearAuthState(preserve?: Pick<AuthState, 'signOutReason'>): void {
    this.storage.clear();
    this.setState({
      jwt: null,
      isAuthenticated: false,
      isLoading: false,
      signingOut: false,
      login: null,
      signOutReason: undefined,
      ...preserve,
    });
  }

  private maybeRefreshToken(): void {
    if (!this.state.jwt || this.isRefreshing) return;
    const now = Date.now();
    if (now - this.lastRefreshAttemptMs < AuthService.REFRESH_COOLDOWN_MS)
      return;
    let expiryMs: number | null;
    try {
      expiryMs = getTokenExpiryMs(this.state.jwt);
    } catch {
      return;
    }
    if (expiryMs == null) return;
    if (
      expiryMs - AuthService.REFRESH_THRESHOLD_MS <= now &&
      expiryMs > now &&
      !this.isDisposed
    ) {
      this.lastRefreshAttemptMs = now;
      void this.performTokenRefresh();
    }
  }

  private async performTokenRefresh(): Promise<void> {
    const jwtAtStart = this.state.jwt;
    this.isRefreshing = true;
    try {
      const response = await this.apiClient.get_Login_Refresh({});
      if (
        !this.isDisposed &&
        response.jwt &&
        this.state.jwt === jwtAtStart &&
        !this.state.signingOut
      ) {
        this.storage.jwt = response.jwt;
        this.setState({ jwt: response.jwt });
      }
    } catch (e) {
      if (!this.isDisposed) {
        logger.warn('Background token refresh failed.', {}, e);
      }
    } finally {
      this.isRefreshing = false;
    }
  }

  private setLoading(isLoading: boolean): void {
    if (this.state.isLoading !== isLoading) {
      this.setState({ isLoading });
    }
  }

  private syncFromStorage(): void {
    const jwt = this.storage.jwt ?? null;
    const login = this.storage.login ?? null;
    if (!jwt && !login) {
      this.clearAuthState();
    } else {
      this.setState({
        jwt,
        login: jwt && login ? { ...login } : null,
        isAuthenticated: !!jwt && !!login,
        signOutReason: undefined,
        isLoading: false,
        signingOut: false,
      });
    }
  }

  private throwAuthError(
    message: string,
    code?: string,
    originalError?: unknown,
  ): never {
    const error = new AuthError(message, code, originalError);

    throw error;
  }
}
